import { useCallback, useContext, useEffect, useState } from "react";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useFetchPostV2 } from "hooks/fetch/useFetchPostV2";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import isNil from "lodash/isNil";
import PropTypes from "prop-types";
import Skeleton from "@mui/material/Skeleton";
import caseNotificationButtonStyle from "assets/jss/default/components/caseNotificationButtonStyle";
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UserProfileContext } from "contexts/UserProfileContext";

const useStyles = makeStyles(caseNotificationButtonStyle);

export default function NotificationsIndicatorButton({ caseId }) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { currentUserId } = useContext(UserProfileContext);

  const classes = useStyles();

  const {
    loading: caseUsersLoading,
    error: caseUsersError,
    response: caseUsers,
    refetch: refetchCaseUsers
  } = useFetchGetV2(`case_user?size=9999&case_id__in=${caseId}`, isNil(caseId));

  const {
    loading: unsubscribeLoading,
    error: unsubscribeError,
    postCallback: unsubscribe
  } = useFetchPostV2(`cases/${caseId}/unsubscribe`, isNil(caseId));

  const {
    loading: subscribeLoading,
    error: subscribeError,
    postCallback: subscribe
  } = useFetchPostV2(`case_user/subscription`);

  useEffect(() => {
    if (caseUsersLoading || caseUsersError || !caseUsers) return;

    const setCurrentUserNotificationStatus = async () => {
      if (currentUserId !== null) {
        const caseUser = caseUsers.results.find(x => x.user === currentUserId);

        if (caseUser && caseUser.notification_needed) {
          setIsSubscribed(true);
        } else {
          setIsSubscribed(false);
        }
      } else {
        setIsSubscribed(false);
      }
    };

    setCurrentUserNotificationStatus().then();
  }, [caseUsersLoading, caseUsersError, caseUsers, currentUserId]);

  const toggleSubscribe = useCallback(async () => {
    try {
      if (isSubscribed) {
        await unsubscribe(caseId);
        await refetchCaseUsers();
        addSnackbarToQueue({
          message: "Notification updates turned off.",
          severity: "success"
        });
      } else {
        await subscribe({
          case_id: caseId,
          subscription_type: 3 // participant
        });
        await refetchCaseUsers();
        addSnackbarToQueue({
          message: "Notification updates turned on.",
          severity: "success"
        });
      }
    } catch (error) {
      console.error(error);
      addSnackbarToQueue({
        message: "Setting notification updates status failed.",
        severity: "error"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, isSubscribed]);

  if (caseUsersLoading || unsubscribeLoading || subscribeLoading)
    return <Skeleton variant="rectangular" width={128} height={40} />;

  if (caseUsersError || unsubscribeError || subscribeError)
    return "Notification status not found";

  return (
    <>
      {isSubscribed ? (
        <Tooltip title="Disable notifications for this case">
          <Button
            variant="outlined"
            color="primary"
            className={classes.notificationButton}
            onClick={toggleSubscribe}
            startIcon={<NotificationsIcon fontSize="large" />}
            aria-label="notifications"
            data-qa="CP-Button-Disable-Notifications"
          >
            Watching
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title="Enable notifications for this case">
          <Button
            variant="outlined"
            color="primary"
            className={classes.notificationButton}
            onClick={toggleSubscribe}
            startIcon={<NotificationsNoneIcon fontSize="large" />}
            aria-label="notifications"
            data-qa="CP-Button-Enable-Notifications"
          >
            Watch Case
          </Button>
        </Tooltip>
      )}
    </>
  );
}

NotificationsIndicatorButton.propTypes = {
  caseId: PropTypes.number
};
